import React, { useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { isEmpty, debounce } from 'underscore';
import PropTypes from 'prop-types';
import {
  SiteConfigContext,
  useClientContext,
} from '@bingads-webui-campaign-react/smart-page-context';
import {
  prependProtocolToUrl,
  getWebsiteSourceDataByKey,
  WEBSITE_SOURCE_DATA_KEY_MAPPING,
} from '@bingads-webui-campaign-react/smart-component-util';

import { SmartVideoView } from './smart-video-view';
import { getDataFromConfig } from './data-config';

export const SmartVideoConnector = ({ id, isEditMode }) => {
  const { siteConfig } = useContext(SiteConfigContext);
  const { isMobile } = useClientContext();
  const componentConfig =
    getWebsiteSourceDataByKey(siteConfig, WEBSITE_SOURCE_DATA_KEY_MAPPING.EmbeddedVideos);
  // Now we only support 1 video.
  const videoConfig = (!isEmpty(componentConfig) && componentConfig[0]) || {};
  const {
    videoUrl,
    videoWidth = 640,
    videoHeight = 360,
    videoDescription,
    videoCanPlay,
  } = getDataFromConfig(videoConfig);

  const [width, setWidth] = useState(`${videoWidth}px`);
  const [height, setHeight] = useState(`${videoHeight}px`);
  const smartVideoContainerRef = useRef(undefined);

  const setResizeValue = useCallback(() => {
    const containerElement = smartVideoContainerRef.current;
    let newWidth = videoWidth;
    let newHeight = videoHeight;

    if (!isEmpty(containerElement)) {
      const containerWidth = parseInt(containerElement.clientWidth, 10);
      if (containerWidth < newWidth) {
        const aspectRatio = newHeight / newWidth;
        // make video width 95% of container width
        newWidth = parseInt(containerWidth * 0.95, 10);
        newHeight = parseInt(aspectRatio * newWidth, 10);
      }

      setWidth(`${newWidth}px`);
      setHeight(`${newHeight}px`);
    }
  }, [videoHeight, videoWidth]);

  const resizeHandler = useMemo(() => debounce(setResizeValue, 50), [setResizeValue]);

  useEffect(() => {
    setResizeValue();
  }, [videoWidth, videoHeight, isMobile, setResizeValue]);

  useEffect(() => {
    window.addEventListener('resize', resizeHandler);

    return () => window.removeEventListener('resize', resizeHandler);
  }, [resizeHandler]);

  return (
    <div
      className="smart-page-embed-video-connector"
      ref={smartVideoContainerRef}
    >
      <SmartVideoView
        videoUrl={prependProtocolToUrl(videoUrl)}
        videoCanPlay={videoCanPlay}
        width={width}
        height={height}
        videoDescription={videoDescription}
        id={id}
        isEditMode={isEditMode}
      />
    </div>
  );
};

SmartVideoConnector.propTypes = {
  id: PropTypes.string.isRequired,
  isEditMode: PropTypes.bool,
};

SmartVideoConnector.defaultProps = {
  isEditMode: false,
};
